import React, { useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { DocumentIcon, XMarkIcon, EyeIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Document } from '../registration/RegistrationWizard';

interface MultipleDocumentUploadProps {
    existingDocuments: Document[];
    onUploadSuccess: (document: Document) => void;
    onDeleteSuccess: (documentId: number) => void;
    disabled?: boolean;
    maxFiles?: number;
}

export const MultipleDocumentUpload: React.FC<MultipleDocumentUploadProps> = ({
    existingDocuments,
    onUploadSuccess,
    onDeleteSuccess,
    disabled = false,
    maxFiles = 10
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const validateFile = (file: File): string | null => {
        // Validate file type
        if (file.type !== 'application/pdf') {
            return 'Разрешены только PDF файлы';
        }

        // Validate file size (50MB max)
        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
            return 'Размер файла не должен превышать 50MB';
        }

        // Check if we've reached the max files limit
        if (existingDocuments.length + uploadingFiles.size >= maxFiles) {
            return `Максимум ${maxFiles} дополнительных документов`;
        }

        return null;
    };

    const uploadFile = async (file: File) => {
        const fileKey = `${file.name}-${file.size}`;
        setUploadingFiles(prev => new Set(prev).add(fileKey));

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'ADDITIONAL_DOCUMENT');

            const response = await apiService.uploadDocument(formData);

            if (response.data.success) {
                const document = (response.data as any).document;
                onUploadSuccess(document);
                toast.success(`Файл ${file.name} загружен успешно`);
            } else {
                toast.error(`Ошибка загрузки ${file.name}`);
            }
        } catch (error: any) {
            console.error('Ошибка загрузки файла:', error);
            toast.error(`Ошибка загрузки ${file.name}: ${error.response?.data?.message || error.message}`);
        } finally {
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(fileKey);
                return newSet;
            });
        }
    };

    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const filesArray = Array.from(files);

        // Check total count
        if (existingDocuments.length + filesArray.length > maxFiles) {
            toast.error(`Можно загрузить максимум ${maxFiles} документов. У вас уже загружено ${existingDocuments.length}.`);
            return;
        }

        // Validate and upload each file
        for (const file of filesArray) {
            const error = validateFile(file);
            if (error) {
                toast.error(`${file.name}: ${error}`);
                continue;
            }

            await uploadFile(file);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(event.target.files);
        // Reset input value to allow uploading the same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragging(true);
        }
    }, [disabled]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (!disabled) {
            handleFiles(e.dataTransfer.files);
        }
    }, [disabled, existingDocuments.length]);

    const handleDelete = async (documentId: number) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот документ?')) {
            return;
        }

        try {
            await apiService.deleteDocument(documentId);
            onDeleteSuccess(documentId);
            toast.success('Документ удален');
        } catch (error: any) {
            console.error('Ошибка удаления документа:', error);
            toast.error('Ошибка удаления документа');
        }
    };

    const handleView = async (doc: Document) => {
        if (doc.fileUrl) {
            window.open(doc.fileUrl, '_blank');
        } else {
            // Если fileUrl нет, получаем его через API
            try {
                const response = await apiService.getDocumentUrl(doc.id);
                if (response.data.success && (response.data as any).url) {
                    window.open((response.data as any).url, '_blank');
                } else {
                    toast.error('Не удалось получить URL документа');
                }
            } catch (error) {
                console.error('Ошибка получения URL документа:', error);
                toast.error('Ошибка получения URL документа');
            }
        }
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const canUploadMore = existingDocuments.length < maxFiles;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">
                    Дополнительные документы
                </h4>
                <span className="text-sm text-gray-500">
                    {existingDocuments.length} из {maxFiles}
                </span>
            </div>

            {/* Upload Zone */}
            {canUploadMore && !disabled && (
                <div
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`
                        relative border-2 border-dashed rounded-lg p-8 text-center transition-all
                        ${isDragging
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-300 hover:border-orange-400 hover:bg-gray-50'
                        }
                        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    onClick={handleButtonClick}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled={disabled}
                    />

                    <ArrowUpTrayIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />

                    <div className="space-y-2">
                        <p className="text-base font-medium text-gray-700">
                            Перетащите файлы сюда или нажмите для выбора
                        </p>
                        <p className="text-sm text-gray-500">
                            Можно загрузить несколько файлов одновременно
                        </p>
                        <p className="text-xs text-gray-400">
                            Только PDF файлы, максимум 50MB каждый
                        </p>
                    </div>

                    <button
                        type="button"
                        className="mt-4 btn-secondary"
                        onClick={handleButtonClick}
                    >
                        Выбрать файлы
                    </button>
                </div>
            )}

            {/* Uploading Progress */}
            {uploadingFiles.size > 0 && (
                <div className="space-y-2">
                    {Array.from(uploadingFiles).map((fileKey) => (
                        <div key={fileKey} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                                <span className="text-sm text-blue-700">Загрузка...</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Uploaded Files List */}
            {existingDocuments.length > 0 && (
                <div className="space-y-2">
                    {existingDocuments.map((doc) => (
                        <div
                            key={doc.id}
                            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 flex-1 min-w-0">
                                    <DocumentIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {doc.fileName}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {formatFileSize(doc.fileSize)} • {new Date(doc.uploadedAt).toLocaleDateString('ru-RU')}
                                        </p>
                                        {doc.status && (
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-2 ${doc.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                                doc.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                                    doc.status === 'PENDING_MODERATION' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                                }`}>
                                                {doc.status === 'APPROVED' && 'Одобрен'}
                                                {doc.status === 'REJECTED' && 'Отклонен'}
                                                {doc.status === 'PENDING_MODERATION' && 'На модерации'}
                                                {doc.status === 'DRAFT' && 'Черновик'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2 ml-4">
                                    <button
                                        type="button"
                                        onClick={() => handleView(doc)}
                                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                        title="Просмотреть"
                                    >
                                        <EyeIcon className="h-5 w-5" />
                                    </button>
                                    {!disabled && (
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(doc.id)}
                                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                            title="Удалить"
                                        >
                                            <XMarkIcon className="h-5 w-5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {existingDocuments.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                    Дополнительные документы не загружены
                </p>
            )}
        </div>
    );
};

